import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { ReactComponent as LongLeftArrow } from "../assets/icons/long-left-arrow.svg";
import { ReactComponent as LongRightArrow } from "../assets/icons/long-right-arrow.svg";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { formatLocationLabel } from "../utils/locationFilters";
import { buildOpenCompanyNavPayload } from "../utils/companyNavigation";

const API_BASE = process.env.REACT_APP_API_BASE;
const RESULTS_PER_PAGE = 8;

const EMPTY_RESULTS = {
  jobs: [],
  leads: [],
  recruiters: [],
  contacts: [],
  candidates: [],
  companies: [],
};
const SEARCH_SECTION_KEYS = Object.keys(EMPTY_RESULTS);

const RESULT_GRID_SX = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    xl: "repeat(4, minmax(0, 1fr))",
  },
};

const RESULT_PILL_SX = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 1.5,
  borderRadius: "22px",
  px: 1.75,
  py: 1.5,
  textAlign: "left",
  border: "1px solid #ececf3",
  backgroundColor: "#f7f7fa",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  transition: "background-color 120ms ease, transform 120ms ease, box-shadow 120ms ease",
  "&:hover": {
    backgroundColor: "#f1f1f7",
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
    transform: "translateY(-1px)",
  },
};

const RESULT_TITLE_SX = {
  fontSize: 15,
  lineHeight: 1.25,
  fontWeight: 700,
  color: "#27272a",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const RESULT_SUBTITLE_SX = {
  mt: 0.25,
  fontSize: 13.5,
  lineHeight: 1.2,
  color: "#71717a",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const SEARCH_PAGINATION_BAR_SX = {
  mt: 2,
  display: "grid",
  gridTemplateColumns: "minmax(90px, 1fr) auto minmax(90px, 1fr)",
  alignItems: "center",
  columnGap: 1.25,
  width: "100%",
  minWidth: 0,
  "@media (max-width:600px)": {
    gridTemplateColumns: "minmax(76px, 1fr) minmax(76px, 1fr)",
    rowGap: 1.25,
    "& .search-pagination-page-numbers": {
      gridColumn: "1 / -1",
      gridRow: 1,
      justifySelf: "center",
    },
    "& .prev-btn": { gridColumn: 1, gridRow: 2, justifySelf: "start" },
    "& .next-btn": { gridColumn: 2, gridRow: 2, justifySelf: "end" },
  },
};

const SEARCH_PAGINATION_NAV_BUTTON_SX = {
  borderRadius: 30,
  px: 2.5,
  py: 0.75,
  minWidth: 90,
  height: 43,
  touchAction: "manipulation",
  boxShadow: "none",
  textTransform: "none",
  fontSize: 13,
};

const SEARCH_PAGINATION_PREV_BUTTON_SX = {
  ...SEARCH_PAGINATION_NAV_BUTTON_SX,
  border: "1px solid #9C4FFF",
  backgroundColor: "#ffffff",
  color: "#9C4FFF",
  justifyContent: "flex-start",
  "&:hover": {
    backgroundColor: "#FFFFFF !important",
    color: "#9C4FFF !important",
    borderColor: "#9C4FFF !important",
    boxShadow: "none !important",
  },
  "& svg": {
    mr: 0.5,
  },
  "& svg path": {
    fill: "#9C4FFF",
  },
  "&.Mui-disabled": {
    borderColor: "#A2ABB4",
    color: "#A2ABB4",
    backgroundColor: "transparent",
  },
  "&.Mui-disabled svg path": {
    fill: "#A2ABB4",
  },
};

const SEARCH_PAGINATION_NEXT_BUTTON_SX = {
  ...SEARCH_PAGINATION_NAV_BUTTON_SX,
  backgroundColor: "#9C4FFF",
  color: "#FFFFFF",
  justifyContent: "flex-end",
  "&:hover": {
    backgroundColor: "#FFFFFF !important",
    color: "#9C4FFF !important",
    border: "1px solid #9C4FFF !important",
    borderColor: "#9C4FFF !important",
    boxShadow: "none !important",
  },
  "& svg": {
    ml: 0.5,
  },
  "& svg path": {
    fill: "#FFFFFF",
  },
  "&:hover svg path": {
    fill: "#9C4FFF !important",
  },
  "&.Mui-disabled": {
    backgroundColor: "transparent",
    border: "1px solid #A2ABB4",
    color: "#A2ABB4",
  },
  "&.Mui-disabled svg path": {
    fill: "#A2ABB4",
  },
};

const SEARCH_PAGINATION_PAGE_BUTTON_SX = {
  minWidth: { xs: 32, sm: 42 },
  width: { xs: 32, sm: 42 },
  height: { xs: 32, sm: 42 },
  flex: { xs: "0 0 32px", sm: "0 0 42px" },
  borderRadius: "50%",
  p: 0,
  touchAction: "manipulation",
  boxShadow: "none",
  lineHeight: "normal",
  textTransform: "none",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid transparent",
  backgroundColor: "#FFFFFF",
  color: "#6B7280",
  "&:hover": {
    backgroundColor: "#FAF7FF",
    borderColor: "#D9C2FF",
    color: "#9747FF",
  },
};

function createInitialSectionPages() {
  return SEARCH_SECTION_KEYS.reduce((pages, key) => {
    pages[key] = 1;
    return pages;
  }, {});
}

function createPaginationItems(currentPage, pageCount) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, "…", pageCount - 1, pageCount];
  }

  if (currentPage >= pageCount - 3) {
    return [1, 2, "…", pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
  }

  return [1, "…", currentPage - 1, currentPage, currentPage + 1, "…", pageCount];
}

function getPageCount(items = [], pageSize = RESULTS_PER_PAGE) {
  return Math.max(1, Math.ceil(items.length / pageSize));
}

function getPaginatedItems(items = [], page = 1, pageSize = RESULTS_PER_PAGE) {
  const startIndex = (Math.max(1, page) - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
}

function safeTrim(value) {
  return value === undefined || value === null ? "" : String(value).trim();
}

function getInitials(value = "") {
  const parts = safeTrim(value).split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function formatRoleLabel(value = "") {
  const normalized = safeTrim(value)
    .replace(/[_-]+/g, " ")
    .toLowerCase();

  if (!normalized) return "";

  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function joinSubtitleParts(parts = []) {
  return parts.map((part) => safeTrim(part)).filter(Boolean).join(" | ");
}

function getRecruiterSubtitle(user = {}) {
  return (
    safeTrim(user?.title) ||
    formatRoleLabel(user?.role) ||
    safeTrim(user?.email) ||
    "Recruiter"
  );
}

function getCandidateSubtitle(candidate = {}) {
  return (
    safeTrim(candidate?.title) ||
    safeTrim(candidate?.status) ||
    safeTrim(candidate?.email) ||
    "Candidate"
  );
}

function getJobSubtitle(job = {}) {
  return (
    joinSubtitleParts([job?.company_name, job?.status]) ||
    safeTrim(job?.location) ||
    "Job"
  );
}

function getLeadSubtitle(lead = {}) {
  return (
    joinSubtitleParts([
      lead?.status,
      formatLocationLabel(lead?.location_city, lead?.location_state, lead?.location_country),
    ]) || "Lead"
  );
}

function getCompanySubtitle(company = {}) {
  return (
    joinSubtitleParts([company?.website, company?.status]) ||
    safeTrim(company?.location) ||
    "Company"
  );
}

function isRecruiterLikeUser(user = {}) {
  const normalizedRole = safeTrim(user?.role).toLowerCase();
  const normalizedTitle = safeTrim(user?.title).toLowerCase();

  if (["recruiter", "admin", "administrator", "system_user"].includes(normalizedRole)) {
    return true;
  }

  return normalizedTitle.includes("recruit");
}

function SectionCard({ title, count, description = null, children }) {
  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={800}>{title}</Typography>
        <Chip size="small" label={count} />
      </Box>
      <Divider sx={{ my: 1 }} />
      {description ? (
        <Typography color="text.secondary" sx={{ mb: 1.5 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Card>
  );
}

function SectionState({
  loading,
  emptyLabel,
  loadingLabel = "AI Agent is still working on the search results.",
}) {
  if (loading) {
    return <Typography color="text.secondary">{loadingLabel}</Typography>;
  }

  return <Typography color="text.secondary">{emptyLabel}</Typography>;
}

function SearchResultPill({
  title,
  subtitle,
  avatarSrc,
  fallbackLabel,
  avatarBg,
  avatarColor,
  avatarContent,
  onClick,
}) {
  const normalizedAvatarSrc = safeTrim(avatarSrc);

  return (
    <ButtonBase onClick={onClick} sx={RESULT_PILL_SX}>
      <Avatar
        src={normalizedAvatarSrc || undefined}
        sx={{
          width: 48,
          height: 48,
          flexShrink: 0,
          bgcolor: normalizedAvatarSrc ? "transparent" : avatarBg,
          color: avatarColor,
          fontWeight: 700,
        }}
      >
        {!normalizedAvatarSrc ? avatarContent || getInitials(fallbackLabel || title) : null}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={RESULT_TITLE_SX}>{title}</Typography>
        <Typography sx={RESULT_SUBTITLE_SX}>{subtitle}</Typography>
      </Box>
    </ButtonBase>
  );
}

function PersonResultPill({
  name,
  subtitle,
  avatarSrc,
  fallbackLabel,
  onClick,
}) {
  return (
    <SearchResultPill
      title={name}
      subtitle={subtitle}
      avatarSrc={avatarSrc}
      fallbackLabel={fallbackLabel}
      avatarBg="#dbeafe"
      avatarColor="#1d4ed8"
      onClick={onClick}
    />
  );
}

function RecruiterResultPill({ recruiter, onClick }) {
  const name =
    safeTrim(recruiter?.name) ||
    safeTrim(recruiter?.full_name) ||
    safeTrim(recruiter?.email) ||
    "Recruiter";

  return (
    <PersonResultPill
      name={name}
      subtitle={getRecruiterSubtitle(recruiter)}
      avatarSrc={recruiter?.profile_image || recruiter?.avatar_url}
      fallbackLabel={name}
      onClick={onClick}
    />
  );
}

function CandidateResultPill({ candidate, onClick }) {
  const name =
    safeTrim(candidate?.name) ||
    safeTrim(candidate?.full_name) ||
    safeTrim(candidate?.email) ||
    "Candidate";

  return (
    <PersonResultPill
      name={name}
      subtitle={getCandidateSubtitle(candidate)}
      avatarSrc={candidate?.avatar_url || candidate?.profile_image}
      fallbackLabel={name}
      onClick={onClick}
    />
  );
}

function ContactResultPill({ contact, onClick }) {
  const name =
    safeTrim(contact?.name) ||
    safeTrim(contact?.full_name) ||
    safeTrim(contact?.email) ||
    "Contact";

  return (
    <PersonResultPill
      name={name}
      subtitle={joinSubtitleParts([contact?.title || "Contact", contact?.company || contact?.email])}
      avatarSrc={contact?.profile_image || contact?.avatar_url}
      fallbackLabel={name}
      onClick={onClick}
    />
  );
}

function JobResultPill({ job, onClick }) {
  return (
    <SearchResultPill
      title={safeTrim(job?.job_title) || "Untitled Job"}
      subtitle={getJobSubtitle(job)}
      avatarSrc={job?.company_logo}
      avatarBg="#e9e8ff"
      avatarColor="#5b21b6"
      avatarContent={<BusinessCenterRoundedIcon sx={{ fontSize: 26 }} />}
      onClick={onClick}
    />
  );
}

function LeadResultPill({ lead, onClick }) {
  return (
    <SearchResultPill
      title={safeTrim(lead?.company_name) || "Untitled Lead"}
      subtitle={getLeadSubtitle(lead)}
      avatarSrc={lead?.company_logo}
      avatarBg="#ede9fe"
      avatarColor="#6d28d9"
      avatarContent={<StorefrontRoundedIcon sx={{ fontSize: 26 }} />}
      onClick={onClick}
    />
  );
}

function CompanyResultPill({ company, onClick }) {
  return (
    <SearchResultPill
      title={safeTrim(company?.name) || "Untitled Company"}
      subtitle={getCompanySubtitle(company)}
      avatarSrc={company?.logo || company?.company_logo}
      avatarBg="#e7f5ec"
      avatarColor="#166534"
      avatarContent={<ApartmentRoundedIcon sx={{ fontSize: 26 }} />}
      onClick={onClick}
    />
  );
}

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = safeTrim(params.get("q"));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(EMPTY_RESULTS);
  const [error, setError] = useState("");
  const [sectionPages, setSectionPages] = useState(() => createInitialSectionPages());

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    "";

  useEffect(() => {
    setSectionPages(createInitialSectionPages());
  }, [q]);

  useEffect(() => {
    setSectionPages((prev) => {
      let hasChanges = false;
      const next = { ...prev };

      SEARCH_SECTION_KEYS.forEach((key) => {
        const currentPage = prev[key] || 1;
        const pageCount = getPageCount(data[key] || []);
        const clampedPage = Math.min(currentPage, pageCount);

        if (clampedPage !== currentPage) {
          next[key] = clampedPage;
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [data]);

  const handleSectionPageChange = (sectionKey, nextPage) => {
    setSectionPages((prev) =>
      prev[sectionKey] === nextPage
        ? prev
        : {
            ...prev,
            [sectionKey]: nextPage,
        }
    );
  };

  const openJobResult = (job) => {
    const jobId = safeTrim(job?.id);
    if (!jobId) {
      navigate("/jobs");
      return;
    }

    navigate(`/jobs?jobId=${encodeURIComponent(jobId)}&tab=details`);
  };

  const openLeadResult = (lead) => {
    const leadId = safeTrim(lead?.lead_id || lead?.leadId || lead?.id);
    if (!leadId) {
      navigate("/leads");
      return;
    }

    navigate(`/leads?leadId=${encodeURIComponent(leadId)}&tab=details`);
  };

  const openCandidateResult = (candidate) => {
    const candidateId = safeTrim(candidate?.candidate_id || candidate?.id);
    if (!candidateId) {
      navigate("/candidates");
      return;
    }

    navigate(`/candidates?candidateId=${encodeURIComponent(candidateId)}&tab=details`);
  };

  const openCompanyResult = (company) => {
    const companyId = safeTrim(company?.company_id || company?.companyId || company?.id);
    if (!companyId) {
      navigate("/companies");
      return;
    }

    const payload = buildOpenCompanyNavPayload({
      company: {
        ...company,
        id: company?.id ?? company?.company_id ?? company?.companyId ?? companyId,
        company_id: company?.company_id ?? company?.companyId ?? company?.id ?? companyId,
        companyId: company?.companyId ?? company?.company_id ?? company?.id ?? companyId,
      },
      defaultTab: "details",
      returnListTab: "companies",
      entityType: "company",
    });

    navigate(
      `/companies?companyId=${encodeURIComponent(companyId)}&tab=details&entityType=company&returnTab=companies`,
      {
        state: payload || undefined,
      }
    );
  };

  const openRecruiterResult = (recruiter) => {
    const recruiterId = safeTrim(recruiter?.id || recruiter?.user_id || recruiter?.userId);
    if (!recruiterId) {
      navigate("/team");
      return;
    }

    navigate(`/users/${encodeURIComponent(recruiterId)}`, {
      state: { recruiter },
    });
  };

  const openContactResult = (contact) => {
    const contactId = safeTrim(contact?.id);
    if (!contactId) {
      navigate("/companies?tab=contacts");
      return;
    }

    navigate(`/contacts/${encodeURIComponent(contactId)}`, {
      state: { contact, companyName: contact?.company },
    });
  };

  const renderPaginatedGrid = (
    sectionKey,
    items,
    renderItem,
    emptyLabel,
    loadingLabel = "AI Agent is still working on the search results."
  ) => {
    if (!items.length) {
      return <SectionState loading={loading} emptyLabel={emptyLabel} loadingLabel={loadingLabel} />;
    }

    const pageCount = getPageCount(items);
    const currentPage = Math.min(sectionPages[sectionKey] || 1, pageCount);
    const pageItems = getPaginatedItems(items, currentPage);
    const paginationItems = createPaginationItems(currentPage, pageCount);

    return (
      <>
        <Box sx={RESULT_GRID_SX}>{pageItems.map(renderItem)}</Box>
        {pageCount > 1 ? (
          <Box sx={SEARCH_PAGINATION_BAR_SX}>
            <Button
              type="button"
              onClick={() => handleSectionPageChange(sectionKey, currentPage - 1)}
              disabled={currentPage === 1}
              className="prev-btn"
              sx={{ ...SEARCH_PAGINATION_PREV_BUTTON_SX, justifySelf: "start" }}
            >
              <LongLeftArrow width={20} height={20} />
              Prev
            </Button>

            <Box className="search-pagination-page-numbers" display="flex" alignItems="center" justifyContent="center" gap={1}>
              {paginationItems.map((item, index) =>
                typeof item === "number" ? (
                  <Button
                    key={`${sectionKey}-page-${item}`}
                    type="button"
                    onClick={() => handleSectionPageChange(sectionKey, item)}
                    sx={{
                      ...SEARCH_PAGINATION_PAGE_BUTTON_SX,
                      ...(currentPage === item
                        ? {
                            borderColor: "#9747FF",
                            color: "#9747FF",
                            backgroundColor: "#FFFFFF",
                          }
                        : null),
                    }}
                  >
                    {item}
                  </Button>
                ) : (
                  <Typography
                    key={`${sectionKey}-ellipsis-${index}`}
                    variant="body2"
                    sx={{ width: { xs: 32, sm: 42 }, flex: { xs: "0 0 32px", sm: "0 0 42px" }, color: "#98A2B3", textAlign: "center" }}
                  >
                    {item}
                  </Typography>
                )
              )}
            </Box>

            <Button
              type="button"
              onClick={() => handleSectionPageChange(sectionKey, currentPage + 1)}
              disabled={currentPage === pageCount}
              className="next-btn"
              sx={{ ...SEARCH_PAGINATION_NEXT_BUTTON_SX, justifySelf: "end" }}
            >
              Next
              <LongRightArrow width={20} height={20} />
            </Button>
          </Box>
        ) : null}
      </>
    );
  };

  useEffect(() => {
    if (!q) {
      setData(EMPTY_RESULTS);
      setError("");
      setLoading(false);
      return;
    }

    const run = async () => {
      setData(EMPTY_RESULTS);
      setLoading(true);
      setError("");

      try {
        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
              "x-access-token": token,
            }
          : {};

        const res = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(q)}`, {
          headers,
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || "Search failed");

        const recruiterMatches = Array.isArray(json.users)
          ? json.users.filter((user) => isRecruiterLikeUser(user))
          : [];

        setData({
          jobs: Array.isArray(json.jobs) ? json.jobs : [],
          leads: Array.isArray(json.leads) ? json.leads : [],
          recruiters: recruiterMatches,
          contacts: Array.isArray(json.contacts) ? json.contacts : [],
          candidates: Array.isArray(json.candidates) ? json.candidates : [],
          companies: Array.isArray(json.companies) ? json.companies : [],
        });
      } catch (nextError) {
        setError(nextError?.message || "Search failed");
        setData(EMPTY_RESULTS);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [q, token]);

  const jobSectionDescription = loading
    ? `AI Agent is still working on the related job search results for ${q || "-"}.`
    : data.jobs.length
      ? `Related job results for ${q || "-"}.`
      : "";

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Search Results
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Query: <b>{q || "-"}</b>
      </Typography>

      {error ? (
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography color="error">{error}</Typography>
        </Card>
      ) : null}

      {loading ? (
        <Card
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid rgba(151, 71, 255, 0.18)",
            background:
              "linear-gradient(135deg, rgba(252, 248, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)",
            boxShadow: "0 10px 28px rgba(109, 40, 217, 0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <CircularProgress
              size={20}
              thickness={5}
              sx={{ mt: "2px", color: "#7c3aed", flexShrink: 0 }}
            />
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#3451F4" }}>
                AI Agent is still working
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.35 }}>
                Searching jobs, leads, recruiters, contacts, candidates, and companies for{" "}
                <b>{q || "-"}</b>. Related job search results will appear here automatically.
              </Typography>
            </Box>
          </Box>
        </Card>
      ) : null}

      <SectionCard
        title="Jobs"
        count={loading ? "..." : data.jobs.length}
        description={jobSectionDescription}
      >
        {renderPaginatedGrid(
          "jobs",
          data.jobs,
          (job) => <JobResultPill key={job.id} job={job} onClick={() => openJobResult(job)} />,
          "No related job results.",
          "AI Agent is still working on the related job search results."
        )}
      </SectionCard>

      <SectionCard title="Leads" count={loading ? "..." : data.leads.length}>
        {renderPaginatedGrid(
          "leads",
          data.leads,
          (lead) => <LeadResultPill key={lead.id} lead={lead} onClick={() => openLeadResult(lead)} />,
          "No lead results."
        )}
      </SectionCard>

      <SectionCard title="Recruiters" count={loading ? "..." : data.recruiters.length}>
        {renderPaginatedGrid(
          "recruiters",
          data.recruiters,
          (recruiter) => (
            <RecruiterResultPill
              key={recruiter.id}
              recruiter={recruiter}
              onClick={() => openRecruiterResult(recruiter)}
            />
          ),
          "No recruiter results."
        )}
      </SectionCard>

      <SectionCard title="Contacts" count={loading ? "..." : data.contacts.length}>
        {renderPaginatedGrid(
          "contacts",
          data.contacts,
          (contact) => (
            <ContactResultPill
              key={contact.id}
              contact={contact}
              onClick={() => openContactResult(contact)}
            />
          ),
          "No contact results."
        )}
      </SectionCard>

      <SectionCard title="Candidates" count={loading ? "..." : data.candidates.length}>
        {renderPaginatedGrid(
          "candidates",
          data.candidates,
          (candidate) => (
            <CandidateResultPill
              key={candidate.id}
              candidate={candidate}
              onClick={() => openCandidateResult(candidate)}
            />
          ),
          "No candidate results."
        )}
      </SectionCard>

      <SectionCard title="Companies" count={loading ? "..." : data.companies.length}>
        {renderPaginatedGrid(
          "companies",
          data.companies,
          (company) => (
            <CompanyResultPill
              key={company.id}
              company={company}
              onClick={() => openCompanyResult(company)}
            />
          ),
          "No company results."
        )}
      </SectionCard>
    </Box>
  );
}
